"use client"

import * as React from "react"
import { Copy, Check, Mail, MessageCircle, Share2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

interface ShareModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  league: {
    id: string
    name: string
    game: string
  }
}

export function ShareModal({ open, onOpenChange, league }: ShareModalProps) {
  const [copied, setCopied] = React.useState(false)
  
  const shareUrl = typeof window !== "undefined" 
    ? `${window.location.origin}/leagues/${league.id}?ref=invite`
    : `/leagues/${league.id}?ref=invite`
  
  const shareText = `Join me in the ${league.name} league for ${league.game} on GameNight League!`

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Join me in ${league.name}!`)
    const body = encodeURIComponent(`${shareText}\n\nSign up here: ${shareUrl}`)
    window.open(`mailto:?subject=${subject}&body=${body}`)
  }

  const shareViaSMS = () => {
    const body = encodeURIComponent(`${shareText} ${shareUrl}`)
    window.open(`sms:?body=${body}`)
  }

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: league.name,
          text: shareText,
          url: shareUrl,
        })
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.error("Share failed:", err)
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground">Invite Friends</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Share this league with friends. When they sign up using your link, you&apos;ll both earn a $5 credit.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* League Info */}
          <div className="bg-secondary/50 rounded-lg p-3">
            <p className="font-medium text-foreground">{league.name}</p>
            <p className="text-sm text-muted-foreground">{league.game}</p>
          </div>

          {/* Copy Link */}
          <div className="flex items-center gap-2">
            <Input 
              value={shareUrl} 
              readOnly 
              className="bg-background/50 border-border text-sm"
            />
            <Button
              onClick={copyToClipboard}
              variant="outline"
              size="icon"
              className="shrink-0 border-border"
            >
              {copied ? (
                <Check className="h-4 w-4 text-primary" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Share Options */}
          <div className="grid grid-cols-3 gap-3">
            <Button
              variant="outline"
              onClick={shareViaEmail}
              className="flex-col gap-2 h-auto py-4 border-border hover:border-primary/50 hover:bg-primary/5"
            >
              <Mail className="h-5 w-5 text-primary" />
              <span className="text-xs">Email</span>
            </Button>
            <Button
              variant="outline"
              onClick={shareViaSMS}
              className="flex-col gap-2 h-auto py-4 border-border hover:border-primary/50 hover:bg-primary/5"
            >
              <MessageCircle className="h-5 w-5 text-primary" />
              <span className="text-xs">Text</span>
            </Button>
            {typeof navigator !== "undefined" && navigator.share && (
              <Button
                variant="outline"
                onClick={shareNative}
                className="flex-col gap-2 h-auto py-4 border-border hover:border-primary/50 hover:bg-primary/5"
              >
                <Share2 className="h-5 w-5 text-primary" />
                <span className="text-xs">More</span>
              </Button>
            )}
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Referral credits apply after your friend completes their first season.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
